import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, UploadCloudIcon } from 'lucide-react';
const steps = [{
  id: 1,
  label: 'Ask'
}, {
  id: 2,
  label: 'Uncover'
}, {
  id: 3,
  label: 'Gap'
}, {
  id: 4,
  label: 'Solution'
}, {
  id: 5,
  label: 'Decision'
}];
export function QuoteModal({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  if (!isOpen) return null;
  const handleNext = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  const resetAndClose = () => {
    setIsSubmitted(false);
    setCurrentStep(1);
    onClose();
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-349">
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={resetAndClose} data-id="element-350"></div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-351">
        {/* Header */}
        <div className="bg-primary p-6 md:p-8 text-white relative shrink-0" data-id="element-352">
          <button onClick={resetAndClose} className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors" data-id="element-353">
            <XIcon className="w-6 h-6" data-id="element-354" />
          </button>
          <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-355">
            Get Your Free Telecom Audit
          </h2>
          <p className="text-primary-100 text-sm md:text-base opacity-90" data-id="element-356">
            Answer a few quick questions so we can identify savings
            opportunities for your business.
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8" data-id="element-357">
          {isSubmitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-12" data-id="element-358">
              <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-359">
                <CheckCircleIcon className="w-10 h-10 text-accent" data-id="element-360" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-361">
                Request Received ✅
              </h3>
              <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-362">
                Thanks for requesting your free telecom audit. Our team will
                review your details and contact you shortly.
              </p>
              <button onClick={resetAndClose} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-363">
                Close
              </button>
            </motion.div> : <>
              {/* Progress Tracker */}
              <div className="mb-8" data-id="element-364">
                <div className="flex items-center justify-between relative" data-id="element-365">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-100 -z-10" data-id="element-366"></div>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-accent transition-all duration-300 -z-10" style={{
                width: `${(currentStep - 1) / 4 * 100}%`
              }} data-id="element-367"></div>
                  {steps.map(step => <div key={step.id} className="flex flex-col items-center gap-2" data-id="element-368">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${currentStep >= step.id ? 'bg-accent text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-369">
                        {step.id}
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-370">
                        {step.label}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Form Steps */}
              <form onSubmit={handleSubmit} data-id="element-371">
                <AnimatePresence mode="wait" data-id="element-372">
                  {currentStep === 1 && <motion.div key="step1" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-373">
                      <div className="mb-6" data-id="element-374">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-375">
                          Tell Us About Your Current Setup
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-376">
                          Lead with a free audit. We review what you're paying
                          first.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-377">
                        <div data-id="element-378">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-379">
                            Business Name
                          </label>
                          <input type="text" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="Acme Corp" data-id="element-380" />
                        </div>
                        <div data-id="element-381">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-382">
                            Full Name
                          </label>
                          <input type="text" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="John Doe" data-id="element-383" />
                        </div>
                        <div data-id="element-384">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-385">
                            Work Email
                          </label>
                          <input type="email" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="john@acmecorp.com" data-id="element-386" />
                        </div>
                        <div data-id="element-387">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-388">
                            Phone Number
                          </label>
                          <input type="tel" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="(555) 123-4567" data-id="element-389" />
                        </div>
                        <div className="md:col-span-2" data-id="element-390">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-391">
                            Current Provider
                          </label>
                          <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="e.g. AT&T, Comcast, Verizon" data-id="element-392" />
                        </div>
                        <div className="md:col-span-2" data-id="element-393">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-394">
                            Upload Current Telecom Bill (PDF/Image)
                          </label>
                          <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center bg-surface hover:bg-slate-100 transition-colors cursor-pointer" data-id="element-395">
                            <UploadCloudIcon className="w-8 h-8 text-slate-400 mb-2" data-id="element-396" />
                            <span className="text-sm text-slate-500" data-id="element-397">
                              Click to upload or drag and drop
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>}

                  {currentStep === 2 && <motion.div key="step2" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-398">
                      <div className="mb-6" data-id="element-399">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-400">
                          Where Might You Be Overpaying?
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-401">
                          Select any that apply to your current situation.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-402">
                        {['Unused phone lines', 'Legacy POTS lines', 'Analog fax lines', 'Expensive monthly bills', 'Poor internet speed', 'Frequent outages', 'Too many features not used'].map((item, i) => <label key={i} className="flex items-center p-4 border border-slate-200 rounded-xl cursor-pointer hover:border-accent transition-colors bg-surface" data-id="element-403">
                            <input type="checkbox" className="w-5 h-5 text-accent rounded border-slate-300 focus:ring-accent" data-id="element-404" />
                            <span className="ml-3 text-sm font-medium text-slate-700" data-id="element-405">
                              {item}
                            </span>
                          </label>)}
                      </div>
                    </motion.div>}

                  {currentStep === 3 && <motion.div key="step3" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-406">
                      <div className="mb-6" data-id="element-407">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-408">
                          Current Spend vs Potential Savings
                        </h3>
                        <p className="text-sm text-accent font-medium mt-1" data-id="element-409">
                          We'll compare your current costs with better options.
                        </p>
                      </div>
                      <div className="space-y-4" data-id="element-410">
                        <div data-id="element-411">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-412">
                            Estimated Monthly Telecom Spend
                          </label>
                          <div className="relative" data-id="element-413">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-414">
                              $
                            </span>
                            <input type="number" className="w-full pl-8 pr-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="0.00" data-id="element-415" />
                          </div>
                        </div>
                        <div data-id="element-416">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-417">
                            Number of Employees
                          </label>
                          <input type="number" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="e.g. 50" data-id="element-418" />
                        </div>
                        <div data-id="element-419">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-420">
                            Number of Locations
                          </label>
                          <input type="number" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="e.g. 3" data-id="element-421" />
                        </div>
                      </div>
                    </motion.div>}

                  {currentStep === 4 && <motion.div key="step4" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-422">
                      <div className="mb-6" data-id="element-423">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-424">
                          What Services Interest You?
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-425">
                          Select the solutions you'd like to explore.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-426">
                        {['VoIP Phone System', 'Business Internet', 'Redundancy / Failover', 'Call Quality Upgrade', 'Multi-location Setup', 'Cabling / Infrastructure', 'Hosted PBX'].map((item, i) => <label key={i} className="flex items-center p-4 border border-slate-200 rounded-xl cursor-pointer hover:border-accent transition-colors bg-surface" data-id="element-427">
                            <input type="checkbox" className="w-5 h-5 text-accent rounded border-slate-300 focus:ring-accent" data-id="element-428" />
                            <span className="ml-3 text-sm font-medium text-slate-700" data-id="element-429">
                              {item}
                            </span>
                          </label>)}
                      </div>
                    </motion.div>}

                  {currentStep === 5 && <motion.div key="step5" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-430">
                      <div className="mb-6" data-id="element-431">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-432">
                          Ready To Save?
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-433">
                          Let us know the best way to reach you.
                        </p>
                      </div>
                      <div className="space-y-4" data-id="element-434">
                        <div data-id="element-435">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-436">
                            Preferred Contact Time
                          </label>
                          <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface" placeholder="e.g. Mornings, After 2 PM" data-id="element-437" />
                        </div>
                        <div data-id="element-438">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-439">
                            How Soon Do You Need This Solved?
                          </label>
                          <select className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface appearance-none" data-id="element-440">
                            <option data-id="element-441">ASAP</option>
                            <option data-id="element-442">This Month</option>
                            <option data-id="element-443">Researching</option>
                          </select>
                        </div>
                        <div data-id="element-444">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-445">
                            Anything else we should know?
                          </label>
                          <textarea rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-surface resize-none" placeholder="Additional details..." data-id="element-446"></textarea>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Footer Actions */}
                <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between" data-id="element-447">
                  {currentStep > 1 ? <button type="button" onClick={handleBack} className="px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors flex items-center gap-2" data-id="element-448">
                      <ArrowLeftIcon className="w-4 h-4" data-id="element-449" /> Back
                    </button> : <div data-id="element-450"></div>}

                  {currentStep < 5 ? <button type="button" onClick={handleNext} className="bg-accent hover:bg-accent-dark text-white px-8 py-3 rounded-xl font-bold transition-colors shadow-lg shadow-accent/20 flex items-center gap-2" data-id="element-451">
                      Next Step <ArrowRightIcon className="w-4 h-4" data-id="element-452" />
                    </button> : <button type="submit" className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-bold transition-colors shadow-lg flex items-center gap-2" data-id="element-453">
                      Submit Free Audit Request
                    </button>}
                </div>
              </form>
            </>}
        </div>
      </motion.div>
    </div>;
}